import {
  Box,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import RemoveIcon from '@mui/icons-material/Remove';

import { fr } from 'date-fns/locale/fr';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AxiosConfig from '../../../config/AxiosConfig';
registerLocale('fr', fr);

const LazyLoading = () => (
  <>
    <TableCell>
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" animation="wave" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" animation="wave" />
    </TableCell>
  </>
);

const UserPerformance = ({ userData }) => {
  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell component="th" scope="row">
        {userData.firstName} {userData.lastName}
      </TableCell>
      <TableCell align="right">
        {userData.dailyActivity?.map((activity, idx) => (
          <Box key={idx}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-evenly',
                position: 'relative',
              }}
            >
              <Typography sx={{ position: 'absolute', left: 0 }}>
                {new Date(activity.date).getDay() === 0 ||
                new Date(activity.date).getDay() === 6 ? (
                  <HomeIcon />
                ) : (
                  ''
                )}
              </Typography>
              <Typography>
                {new Date(activity.date)?.toLocaleString('fr-FR', {
                  month: 'numeric',
                  day: 'numeric',
                })}
              </Typography>
              <Typography>{activity.ticketsCount}</Typography>
              <Box sx={{ position: 'absolute', right: 100 }}>
                {idx > 0 ? (
                  activity.ticketsCount >
                  userData.dailyActivity[idx - 1]?.ticketsCount ? (
                    <ArrowDropUpIcon color="success" />
                  ) : activity.ticketsCount ===
                    userData.dailyActivity[idx - 1]?.ticketsCount ? (
                    <RemoveIcon color="info" />
                  ) : (
                    <ArrowDropDownIcon color="error" />
                  )
                ) : (
                  <RemoveIcon color="info" />
                )}
              </Box>
              <br />
            </Box>
            <Divider />
          </Box>
        ))}
      </TableCell>
      <TableCell align="right">
        {userData.dailyActivity?.map((activity, idx) => (
          <Box key={idx}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-evenly',
                position: 'relative',
              }}
            >
              <Typography>{activity.messagesCount}</Typography>
              <Box sx={{ position: 'absolute', right: 230 }}>
                {idx > 0 ? (
                  activity.messagesCount >
                  userData.dailyActivity[idx - 1]?.messagesCount ? (
                    <ArrowDropUpIcon color="success" />
                  ) : activity.messagesCount ===
                    userData.dailyActivity[idx - 1]?.messagesCount ? (
                    <RemoveIcon color="info" />
                  ) : (
                    <ArrowDropDownIcon color="error" />
                  )
                ) : (
                  <RemoveIcon color="info" />
                )}
              </Box>
              <br />
            </Box>
            <Divider />
          </Box>
        ))}
      </TableCell>
    </TableRow>
  );
};

const PerformanceTab = ({ sensitiveDataAllowedToUser }) => {
  const profile = useSelector((state) => state?.login?.profile);
  const users = useSelector((state) => state?.static?.users);
  const [startDate, setStartDate] = useState(
    new Date().setDate(new Date().getDate() - 6)
  );
  const [endDate, setEndDate] = useState(new Date());

  const [dateRange, setDateRange] = useState([startDate, endDate]);
  const [aggregatedStats, setAggregatedStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const usersToDisplay = useMemo(() => {
    return users
      .filter(
        (user) => sensitiveDataAllowedToUser || profile?._id === user?._id
      )
      .map((user) => user._id);
  }, [users, sensitiveDataAllowedToUser, profile?._id]);

  useEffect(() => {
    const fetchAggregatedStats = async () => {
      setIsLoading(true);
      try {
        await AxiosConfig.create('post', `/stats/operatorPerformance`)
          .call({ dateRange, userIds: usersToDisplay })
          .exit((response) => {
            setAggregatedStats(response?.data || []);
          });
      } catch (error) {
        console.error('Error fetching aggregated users stats:', error);
        setAggregatedStats([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (usersToDisplay.length > 0) {
      fetchAggregatedStats();
    } else {
      setAggregatedStats([]);
      setIsLoading(false);
    }
  }, [dateRange, usersToDisplay]);

  const DateRangePicker = forwardRef(({ _value, onClick }, ref) => (
    <Button
      variant="outlined"
      onClick={onClick}
      ref={ref}
      startIcon={<CalendarTodayIcon />}
      sx={{ width: 240 }}
    >
      {new Date(startDate)?.toLocaleString('fr-FR', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })}{' '}
      -{' '}
      {new Date(endDate)?.toLocaleString('fr-FR', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })}
    </Button>
  ));

  return (
    <Box sx={{ width: 1250 }}>
      <TableContainer
        component={Paper}
        sx={{ marginY: 2, height: '83vh', overflowY: 'scroll' }}
      >
        <Divider sx={{ margin: 2 }}>Performances</Divider>
        <Box sx={{ margin: 1, display: 'flex' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="p">Sélectionner les dates</Typography>
            <ReactDatePicker
              dateFormat="dd/MM/yyyy"
              locale="fr"
              selected={startDate}
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(dates) => {
                const [start, end] = dates;
                setStartDate(start);
                setEndDate(end);
                setDateRange([start, end]);
              }}
              maxDate={new Date()}
              customInput={<DateRangePicker />}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ height: 26 }} />
            <Tooltip title="Revenir à cette semaine" placement="top">
              <Button
                color="error"
                onClick={() => {
                  setStartDate(new Date().setDate(new Date().getDate() - 6));
                  setEndDate(new Date());
                  setDateRange([
                    new Date().setDate(new Date().getDate() - 6),
                    new Date(),
                  ]);
                }}
                size="small"
              >
                <ClearIcon />
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateurs</TableCell>
              <TableCell align="center">Nombre de tickets traités</TableCell>
              <TableCell align="center">Nombre de messages envoyés</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: usersToDisplay.length }).map(
                  (_, index) => (
                    <TableRow key={index}>
                      <LazyLoading />
                    </TableRow>
                  )
                )
              : aggregatedStats?.map((userData) => (
                  <UserPerformance key={userData._id} userData={userData} />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PerformanceTab;
